import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { useConfirm } from '../contexts/ConfirmContext';
import { OverlayPanel } from '../ui/OverlayPanel';
import { FACTION_INFO } from '../constants/factions';

// ============ TYPES ============

interface ModuleStats {
  cannonDamageBonus?: number;
  cannonDamagePercent?: number;
  reloadSpeedBonus?: number;
  cannonRangeBonus?: number;
  critChanceBonus?: number;
  explosiveChance?: number;
  hullHpBonus?: number;
  hullHpPercent?: number;
  armorBonus?: number;
  armorPercent?: number;
  damageReduction?: number;
  fireResistance?: number;
  repairBonus?: number;
  speedBonus?: number;
  speedPercent?: number;
  turnRateBonus?: number;
  sailHpBonus?: number;
  sailHpPercent?: number;
  cargoCapacity?: number;
  crewCapacity?: number;
  xpGainBonus?: number;
  pieceGainBonus?: number;
}

interface ShipModule {
  id: string;
  name: string;
  description: string;
  slotType: 'armament' | 'hull' | 'rigging';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  stats: ModuleStats;
  crewCost: number;   // Crew skill required to operate
  cargoCost: number;  // Cargo space consumed
  buyPrice: number;
  sellPrice: number;
  icon: string;
  tier: number;
}

interface ShipFitting {
  shipId: string;
  modules: {
    armament: (ShipModule | null)[];
    hull: (ShipModule | null)[];
    rigging: (ShipModule | null)[];
  };
  usedCrew: number;
  usedCargo: number;
  maxCrew: number;
  maxCargo: number;
  slotCounts: {
    armament: number;
    hull: number;
    rigging: number;
  };
  stats: ModuleStats;
}

interface CrewTrait {
  id: string;
  name: string;
  description: string;
  effectPerLevel: number;
  statAffected: string;
}

interface CrewFlaw {
  id: string;
  name: string;
  description: string;
  penalty: number;
  condition?: string;
}

interface CrewMember {
  id: string;
  name: string;
  portrait: string;
  level: number;
  xp: number;
  xpToNextLevel: number;
  trait: CrewTrait;
  flaw: CrewFlaw;
  synergyTag?: string;
}

interface ShipFittingPanelProps {
  socket: Socket | null;
  isOpen: boolean;
  onClose: () => void;
  shipId: string;
  shipType: string;
  pieces: number;
  isDockedAtShipyard?: boolean;
}

// ============ CONSTANTS ============

const RARITY_COLORS: Record<string, string> = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
};

const SLOT_TYPE_COLORS: Record<string, string> = {
  armament: '#e74c3c',
  hull: '#3498db',
  rigging: '#2ecc71',
};

const SLOT_TYPE_LABELS: Record<string, string> = {
  armament: 'Armament',
  hull: 'Hull',
  rigging: 'Rigging',
};

const STAT_LABELS: Record<string, string> = {
  cannonDamageBonus: 'Cannon Damage',
  cannonDamagePercent: 'Cannon Damage %',
  reloadSpeedBonus: 'Reload Speed',
  cannonRangeBonus: 'Cannon Range',
  critChanceBonus: 'Crit Chance',
  explosiveChance: 'Fire Chance',
  hullHpBonus: 'Hull HP',
  hullHpPercent: 'Hull HP %',
  armorBonus: 'Armor',
  armorPercent: 'Armor %',
  damageReduction: 'Damage Reduction',
  fireResistance: 'Fire Resistance',
  repairBonus: 'Repair Bonus',
  speedBonus: 'Speed',
  speedPercent: 'Speed %',
  turnRateBonus: 'Turn Rate',
  sailHpBonus: 'Sail HP',
  sailHpPercent: 'Sail HP %',
  cargoCapacity: 'Cargo Slots',
  crewCapacity: 'Crew Capacity',
  xpGainBonus: 'XP Gain',
  pieceGainBonus: 'Piece Gain',
};

const formatCrewBonusLabel = (key: string): string => {
  const explicit: Record<string, string> = {
    hpRegen: 'HP Regen',
    xpBonus: 'XP Bonus',
    lootBonus: 'Loot Bonus',
    shipSpeed: 'Ship Speed',
    turnRate: 'Turn Rate',
    detectionRange: 'Detection Range',
    cannonAccuracy: 'Cannon Accuracy',
    cannonDamage: 'Cannon Damage',
    sailRepair: 'Sail Repair',
    sailDefense: 'Sail Defense',
    allStats: 'All Stats',
  };

  if (explicit[key]) return explicit[key];

  const spaced = key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();

  const words = spaced.split(/\s+/g);
  const normalized = words
    .map((w) => {
      const upper = w.toUpperCase();
      if (upper === 'HP') return 'HP';
      if (upper === 'XP') return 'XP';
      if (upper === 'AOE') return 'AOE';
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(' ');

  return normalized;
};

const getIconSrc = (icon: string | undefined): string => {
  const publicUrl: string = process.env.PUBLIC_URL || '';
  return icon && icon.startsWith('/assets')
    ? `${publicUrl}${icon}`
    : `${publicUrl}/assets/unknown.png`;
};

const getPortraitSrc = (portrait: string | undefined): string => {
  const publicUrl: string = process.env.PUBLIC_URL || '';
  return portrait && portrait.startsWith('/assets')
    ? `${publicUrl}${portrait}`
    : `${publicUrl}/assets/unknown.png`;
};

// ============ STYLES ============

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    fontFamily: 'var(--font-mono), monospace',
  },
  panel: {
    backgroundColor: '#0d1117',
    border: '2px solid #30363d',
    width: '900px',
    maxWidth: '95vw',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    color: '#e6edf3',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #30363d',
    backgroundColor: '#161b22',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#58a6ff',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#8b949e',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '0 8px',
  },
  content: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  leftPanel: {
    flex: 1,
    padding: '16px',
    borderRight: '1px solid #30363d',
    overflowY: 'auto',
  },
  rightPanel: {
    width: '280px',
    padding: '16px',
    overflowY: 'auto',
    backgroundColor: '#161b22',
  },
  resourceBar: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    padding: '8px 12px',
    backgroundColor: '#21262d',
    border: '1px solid #30363d',
  },
  resourceItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
  },
  slotSection: {
    marginBottom: '20px',
  },
  slotHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  slotGrid: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  slot: {
    width: '64px',
    height: '64px',
    border: '2px solid #30363d',
    backgroundColor: '#21262d',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'relative',
    transition: 'border-color 0.2s',
  },
  slotEmpty: {
    color: '#484f58',
    fontSize: '24px',
  },
  slotIcon: {
    width: '40px',
    height: '40px',
    objectFit: 'contain',
  },
  slotCost: {
    position: 'absolute',
    bottom: '2px',
    right: '2px',
    fontSize: '9px',
    color: '#8b949e',
    backgroundColor: 'rgba(0,0,0,0.7)',
    padding: '1px 3px',
  },
  moduleInventory: {
    marginTop: '16px',
  },
  inventoryHeader: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: '#8b949e',
  },
  inventoryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '6px',
  },
  inventorySlot: {
    width: '48px',
    height: '48px',
    border: '1px solid #30363d',
    backgroundColor: '#21262d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  inventoryIcon: {
    width: '32px',
    height: '32px',
    objectFit: 'contain',
  },
  statsSection: {
    marginBottom: '16px',
  },
  statsHeader: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: '#58a6ff',
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    padding: '4px 0',
    borderBottom: '1px solid #21262d',
  },
  statLabel: {
    color: '#8b949e',
  },
  statValue: {
    color: '#2ecc71',
    fontWeight: 'bold',
  },
  selectedModule: {
    padding: '12px',
    backgroundColor: '#21262d',
    border: '1px solid #30363d',
    marginBottom: '12px',
  },
  moduleName: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '4px',
  },
  moduleDesc: {
    fontSize: '11px',
    color: '#8b949e',
    marginBottom: '8px',
  },
  moduleStats: {
    fontSize: '11px',
  },
  actionBtn: {
    width: '100%',
    padding: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '12px',
    marginTop: '8px',
    fontFamily: 'inherit',
  },
  installBtn: {
    backgroundColor: '#238636',
    color: '#fff',
  },
  uninstallBtn: {
    backgroundColor: '#da3633',
    color: '#fff',
  },
  tabBar: {
    display: 'flex',
    borderBottom: '1px solid #30363d',
    backgroundColor: '#161b22',
  },
  tab: {
    padding: '10px 20px',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    color: '#8b949e',
    fontSize: '13px',
    fontFamily: 'inherit',
    borderBottom: '2px solid transparent',
  },
  tabActive: {
    color: '#58a6ff',
    borderBottomColor: '#58a6ff',
  },
  message: {
    padding: '8px 12px',
    backgroundColor: '#21262d',
    borderLeft: '3px solid #58a6ff',
    marginBottom: '12px',
    fontSize: '12px',
  },
};

// ============ COMPONENT ============

// Helper to render item icon with fallback
const renderItemIcon = (icon: string, name: string, size: number = 32): JSX.Element => {
  const publicUrl: string = process.env.PUBLIC_URL || '';
  const fallbackChain = [
    `${publicUrl}/assets/unknown.png`,
  ];
  let currentFallbackIndex = 0;
  
  return (
    <img
      src={getIconSrc(icon)}
      alt={name}
      style={{ width: size, height: size, objectFit: 'contain' }}
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        currentFallbackIndex++;
        
        if (currentFallbackIndex < fallbackChain.length) {
          const nextFallback = fallbackChain[currentFallbackIndex];
          if (target.src !== nextFallback) {
            target.src = nextFallback;
            return;
          }
        }
        
        target.onerror = null;
      }}
    />
  );
};

export const ShipFittingPanel: React.FC<ShipFittingPanelProps> = ({
  socket,
  isOpen,
  onClose,
  shipId,
  shipType,
  pieces,
  isDockedAtShipyard = false,
}) => {
  const { confirm } = useConfirm();
  const [activeTab, setActiveTab] = useState<'overview' | 'crew' | 'inventory' | 'shop'>('overview');
  const [fitting, setFitting] = useState<ShipFitting | null>(null);
  const [moduleInventory, setModuleInventory] = useState<ShipModule[]>([]);
  const [shopModules, setShopModules] = useState<(ShipModule & { stock: number })[]>([]);
  const [selectedModule, setSelectedModule] = useState<ShipModule | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<{ type: 'armament' | 'hull' | 'rigging'; index: number } | null>(null);
  const [message, setMessage] = useState<string>('');
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [maxCrewSlots, setMaxCrewSlots] = useState<number>(0);
  const [selectedCrewId, setSelectedCrewId] = useState<string | null>(null);
  const [crewBonuses, setCrewBonuses] = useState<Record<string, number>>({});
  const [equippedSigil, setEquippedSigil] = useState<string | null>(null);
  const [sigilGrade, setSigilGrade] = useState<number | null>(null);
  const [quickEquipPanelOpen, setQuickEquipPanelOpen] = useState<boolean>(false);
  const [quickEquipSlot, setQuickEquipSlot] = useState<{ type: 'armament' | 'hull' | 'rigging'; index: number } | null>(null);

  const showMessage = useCallback((msg: string): void => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  }, []);

  // Socket listeners
  useEffect(() => {
    if (!socket) return;

    const handleFitting = (data: ShipFitting): void => {
      setFitting(data);
    };

    const handleModuleInventory = (data: { modules: ShipModule[] }): void => {
      setModuleInventory(data.modules);
    };

    const handleModuleShop = (data: { modules: (ShipModule & { stock: number })[] }): void => {
      setShopModules(data.modules);
    };

    const handleCrewState = (data: { crew: CrewMember[]; maxSlots: number; bonuses: Record<string, number> }): void => {
      setCrew(data.crew);
      setMaxCrewSlots(data.maxSlots);
      setCrewBonuses(data.bonuses || {});
    };

    const handleDismissResult = (result: { success: boolean; message: string }): void => {
      showMessage(result.message);
      if (result.success) {
        setSelectedCrewId(null);
      }
    };

    const handleEquippedSigil = (data: { faction: string | null; grade: number | null } | null): void => {
      if (data) {
        setEquippedSigil(data.faction);
        setSigilGrade(data.grade);
      } else {
        setEquippedSigil(null);
        setSigilGrade(null);
      }
    };

    const handleFactionUpdate = (data: any): void => {
      if (data.equippedSigil) {
        setEquippedSigil(data.equippedSigil);
        if (data.sigilGrade) {
          setSigilGrade(data.sigilGrade.grade);
        }
      }
    };

    const handleSigilResult = (result: { success: boolean; message: string }): void => {
      showMessage(result.message);
      if (result.success) {
        socket.emit('faction:getEquippedSigil');
      }
    };

    socket.on('ship:fitting', handleFitting);
    socket.on('modules:inventory', handleModuleInventory);
    socket.on('module_shop:data', handleModuleShop);
    socket.on('crew:state', handleCrewState);
    socket.on('shipcrew:dismissResult', handleDismissResult);
    socket.on('faction:equippedSigil', handleEquippedSigil);
    socket.on('faction:update', handleFactionUpdate);
    socket.on('faction:equipSigil:result', handleSigilResult);
    socket.on('faction:unequipSigil:result', handleSigilResult);

    return () => {
      socket.off('ship:fitting', handleFitting);
      socket.off('modules:inventory', handleModuleInventory);
      socket.off('module_shop:data', handleModuleShop);
      socket.off('crew:state', handleCrewState);
      socket.off('shipcrew:dismissResult', handleDismissResult);
      socket.off('faction:equippedSigil', handleEquippedSigil);
      socket.off('faction:update', handleFactionUpdate);
      socket.off('faction:equipSigil:result', handleSigilResult);
      socket.off('faction:unequipSigil:result', handleSigilResult);
    };
  }, [socket, showMessage]);

  // Request data when opened
  useEffect(() => {
    if (isOpen && socket) {
      socket.emit('ship:get_fitting', { shipId, shipType });
      socket.emit('modules:get_inventory');
      socket.emit('module_shop:get_data');
      socket.emit('shipcrew:request');
      socket.emit('faction:getEquippedSigil');
    }
  }, [isOpen, socket, shipId, shipType]);

  const handleSlotClick = (slotType: 'armament' | 'hull' | 'rigging', index: number): void => {
    const module = fitting?.modules[slotType][index];
    if (module) {
      setSelectedModule(module);
      setSelectedSlot({ type: slotType, index });
    } else {
      // Open quick equip panel for empty slots
      setQuickEquipSlot({ type: slotType, index });
      setQuickEquipPanelOpen(true);
      setSelectedSlot({ type: slotType, index });
      setSelectedModule(null);
    }
  };

  const handleModuleClick = (module: ShipModule): void => {
    setSelectedModule(module);
  };

  const handleInstall = (): void => {
    if (!socket || !selectedModule || !selectedSlot) return;
    socket.emit('modules:install', {
      shipId,
      shipType,
      moduleId: selectedModule.id,
      slotType: selectedSlot.type,
      slotIndex: selectedSlot.index,
    }, (response: { success: boolean; message: string }) => {
      showMessage(response.message);
      if (response.success) {
        setSelectedModule(null);
        setSelectedSlot(null);
      }
    });
  };

  const handleUninstall = (): void => {
    if (!socket || !selectedSlot) return;
    socket.emit('modules:uninstall', {
      shipId,
      shipType,
      slotType: selectedSlot.type,
      slotIndex: selectedSlot.index,
    }, (response: { success: boolean; message: string }) => {
      showMessage(response.message);
      if (response.success) {
        setSelectedModule(null);
        setSelectedSlot(null);
      }
    });
  };

  const handleBuyModule = (moduleId: string): void => {
    if (!socket) return;
    socket.emit('modules:buy', { moduleId }, (response: { success: boolean; message: string }) => {
      showMessage(response.message);
    });
  };

  const handleSellModule = (moduleId: string): void => {
    if (!socket) return;
    socket.emit('modules:sell', { moduleId }, (response: { success: boolean; message: string }) => {
      showMessage(response.message);
    });
  };

  const handleDismissCrew = useCallback((crewId: string, crewName: string): void => {
    if (!socket) return;
    void (async () => {
      const ok: boolean = await confirm({
        title: 'Dismiss Crew',
        message: `Dismiss ${crewName}? This cannot be undone.`,
        confirmText: 'Dismiss',
        cancelText: 'Cancel',
        tone: 'danger',
      });
      if (!ok) return;
      socket.emit('shipcrew:dismiss', { crewId });
    })();
  }, [socket, confirm]);

  const formatStat = (key: string, value: number): string => {
    if (key.includes('Percent') || key.includes('Chance') || key.includes('Resistance') || key.includes('Reduction') || key.includes('Bonus')) {
      if (key === 'reloadSpeedBonus') {
        return `${value > 0 ? '+' : ''}${value}%`;
      }
      if (value < 1 && value > 0) {
        return `+${(value * 100).toFixed(0)}%`;
      }
      return `+${value}%`;
    }
    return `+${value}`;
  };

  const renderSlot = (slotType: 'armament' | 'hull' | 'rigging', index: number): JSX.Element => {
    const module = fitting?.modules[slotType][index];
    const isSelected = selectedSlot?.type === slotType && selectedSlot?.index === index;
    const borderColor = module ? RARITY_COLORS[module.rarity] : (isSelected ? '#58a6ff' : '#30363d');

    return (
      <div
        key={`${slotType}-${index}`}
        style={{ ...styles.slot, borderColor }}
        onClick={() => handleSlotClick(slotType, index)}
        title={module ? `${module.name} (click to select)` : 'Empty slot (click to select)'}
      >
        {module ? (
          <>
            {renderItemIcon(module.icon, module.name, 40)}
            <div style={styles.slotCost}>
              {module.crewCost}/{module.cargoCost}
            </div>
          </>
        ) : (
          <span style={styles.slotEmpty}>+</span>
        )}
      </div>
    );
  };

  const renderModuleStats = (stats: ModuleStats): JSX.Element => {
    const entries = Object.entries(stats).filter(([, v]) => v !== undefined && v !== 0);
    if (entries.length === 0) return <span style={{ color: '#8b949e' }}>No stats</span>;

    return (
      <div style={styles.moduleStats}>
        {entries.map(([key, value]) => (
          <div key={key} style={styles.statRow}>
            <span style={styles.statLabel}>{STAT_LABELS[key] || key}</span>
            <span style={{ ...styles.statValue, color: value! > 0 ? '#2ecc71' : '#e74c3c' }}>
              {formatStat(key, value!)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <OverlayPanel
      isOpen={isOpen}
      onClose={onClose}
      draggable
      storageKey="fittingPanelPosition"
      defaultPosition={{ x: 350, y: 100 }}
      closeOnBackdrop={false}
      title="Ship Fitting"
      width={800}
    >
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header} onClick={(e) => e.stopPropagation()}>
          <h2 style={{...styles.title}} className="font-lookout-24">Ship Overview - {shipType.replace('_', ' ').toUpperCase()}</h2>
          <span style={{ color: '#f0b429', marginRight: 'auto', marginLeft: '16px' }}>
            {pieces.toLocaleString()}₱
          </span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tab Bar */}
        <div style={styles.tabBar}>
          <button
            style={{ ...styles.tab, ...(activeTab === 'overview' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'crew' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('crew')}
          >
            Crew ({crew.length}{maxCrewSlots > 0 ? `/${maxCrewSlots}` : ''})
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'inventory' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('inventory')}
          >
            Module Inventory ({moduleInventory.length})
          </button>
          {isDockedAtShipyard && (
            <button
              style={{ ...styles.tab, ...(activeTab === 'shop' ? styles.tabActive : {}) }}
              onClick={() => setActiveTab('shop')}
            >
              Shipyard Shop
            </button>
          )}
        </div>

        {/* Message */}
        {message && <div style={styles.message}>{message}</div>}

        {/* Content */}
        <div style={styles.content}>
          {/* Left Panel */}
          <div style={styles.leftPanel}>
            {activeTab === 'overview' && fitting && (
              <>
                {/* Resource Bar */}
                <div style={styles.resourceBar}>
                  <div style={styles.resourceItem}>
                    <span style={{ color: '#f39c12' }}>Crew Skill:</span>
                    <span style={{ color: fitting.usedCrew > fitting.maxCrew ? '#e74c3c' : '#2ecc71' }}>
                      {fitting.usedCrew}/{fitting.maxCrew}
                    </span>
                  </div>
                  <div style={styles.resourceItem}>
                    <span style={{ color: '#9b59b6' }}>Cargo Load:</span>
                    <span style={{ color: fitting.usedCargo > fitting.maxCargo ? '#e74c3c' : '#2ecc71' }}>
                      {fitting.usedCargo}/{fitting.maxCargo}
                    </span>
                  </div>
                </div>

                {/* Slot Sections */}
                {(['armament', 'hull', 'rigging'] as const).map((slotType) => (
                  <div key={slotType} style={styles.slotSection}>
                    <div style={styles.slotHeader}>
                      <span style={{ color: SLOT_TYPE_COLORS[slotType] }}>■</span>
                      <span>{SLOT_TYPE_LABELS[slotType]} Slots</span>
                      <span style={{ color: '#8b949e', fontSize: '12px' }}>
                        ({fitting.slotCounts[slotType]} slots)
                      </span>
                    </div>
                    <div style={styles.slotGrid}>
                      {Array.from({ length: fitting.slotCounts[slotType] }).map((_, i) =>
                        renderSlot(slotType, i)
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}

            {activeTab === 'crew' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{
                  padding: '10px 12px',
                  backgroundColor: '#21262d',
                  border: '1px solid #30363d',
                  color: '#8b949e',
                  fontSize: 12,
                }}>
                  Manage your current crew here. Hiring is only available at taverns.
                </div>

                {crew.length === 0 ? (
                  <div style={{ color: '#8b949e', padding: '20px', textAlign: 'center' }}>
                    No crewmates hired.
                  </div>
                ) : (
                  crew.map((member: CrewMember) => {
                    const isSelected: boolean = selectedCrewId === member.id;
                    const xpPercent: number = member.xpToNextLevel > 0
                      ? Math.max(0, Math.min(100, Math.floor((member.xp / member.xpToNextLevel) * 100)))
                      : 0;
                    return (
                      <div
                        key={member.id}
                        style={{
                          backgroundColor: isSelected ? '#161b22' : '#0d1117',
                          border: `1px solid ${isSelected ? '#58a6ff' : '#30363d'}`,
                          padding: 12,
                          cursor: 'pointer',
                        }}
                        onClick={() => setSelectedCrewId(isSelected ? null : member.id)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0 }}>
                            <img
                              src={getPortraitSrc(member.portrait)}
                              alt={member.name}
                              style={{ width: 40, height: 40, objectFit: 'cover', border: '1px solid #30363d' }}
                              onError={(e) => {
                                const publicUrl: string = process.env.PUBLIC_URL || '';
                                const target = e.target as HTMLImageElement;
                                target.onerror = null;
                                target.src = `${publicUrl}/assets/unknown.png`;
                              }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#e6edf3' }}>{member.name}</div>
                              <div style={{ fontSize: 10, color: '#8b949e', marginTop: 2 }}>
                                Level {member.level}
                                {' | '}
                                {member.trait?.name || 'Unknown Trait'}
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDismissCrew(member.id, member.name);
                            }}
                            style={{
                              backgroundColor: '#21262d',
                              border: '1px solid rgba(239, 68, 68, 0.45)',
                              color: '#ef4444',
                              padding: '6px 10px',
                              cursor: 'pointer',
                              fontSize: 10,
                              fontWeight: 700,
                            }}
                          >
                            DISMISS
                          </button>
                        </div>

                        {isSelected && (
                          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ fontSize: 10, color: '#8b949e' }}>
                              XP: {member.xp}/{member.xpToNextLevel} ({xpPercent}%)
                            </div>
                            <div style={{ height: 6, border: '1px solid #30363d', backgroundColor: '#0d1117' }}>
                              <div style={{ height: '100%', width: `${xpPercent}%`, backgroundColor: '#58a6ff' }} />
                            </div>
                            {member.synergyTag && (
                              <div style={{ fontSize: 10, color: '#8b949e' }}>Synergy: {member.synergyTag}</div>
                            )}
                            {member.trait && (
                              <div style={{ fontSize: 10, color: '#c9d1d9' }}>
                                <span style={{ color: '#22c55e', fontWeight: 700 }}>Trait:</span> {member.trait.name} ({member.trait.effectPerLevel}%/lvl)
                                <div style={{ color: '#8b949e', marginTop: 2 }}>{member.trait.description}</div>
                              </div>
                            )}
                            {member.flaw && (
                              <div style={{ fontSize: 10, color: '#c9d1d9' }}>
                                <span style={{ color: '#ef4444', fontWeight: 700 }}>Flaw:</span> {member.flaw.name} (-{member.flaw.penalty}%)
                                <div style={{ color: '#8b949e', marginTop: 2 }}>{member.flaw.description}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {activeTab === 'inventory' && (
              <div style={styles.inventoryGrid}>
                {moduleInventory.map((mod, i) => (
                  <div
                    key={`${mod.id}-${i}`}
                    style={{
                      ...styles.inventorySlot,
                      borderColor: selectedModule?.id === mod.id ? '#58a6ff' : RARITY_COLORS[mod.rarity],
                    }}
                    onClick={() => handleModuleClick(mod)}
                    title={mod.name}
                  >
                    {renderItemIcon(mod.icon, mod.name, 32)}
                  </div>
                ))}
                {moduleInventory.length === 0 && (
                  <div style={{ gridColumn: '1 / -1', color: '#8b949e', padding: '20px', textAlign: 'center' }}>
                    No modules in inventory
                  </div>
                )}
              </div>
            )}

            {activeTab === 'shop' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                {shopModules.map((mod) => (
                  <div
                    key={mod.id}
                    style={{
                      padding: '12px',
                      backgroundColor: '#21262d',
                      border: `1px solid ${RARITY_COLORS[mod.rarity]}`,
                      cursor: 'pointer',
                    }}
                    onClick={() => handleModuleClick(mod)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      {renderItemIcon(mod.icon, mod.name, 32)}
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 'bold', color: RARITY_COLORS[mod.rarity] }}>
                          {mod.name}
                        </div>
                        <div style={{ fontSize: '11px', color: '#8b949e' }}>
                          {SLOT_TYPE_LABELS[mod.slotType]} | Stock: {mod.stock}
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: '12px', color: '#f0b429', marginBottom: '8px' }}>
                      ₱{mod.buyPrice.toLocaleString()}
                    </div>
                    <button
                      style={{
                        ...styles.actionBtn,
                        ...styles.installBtn,
                        opacity: mod.stock <= 0 || pieces < mod.buyPrice ? 0.5 : 1,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBuyModule(mod.id);
                      }}
                      disabled={mod.stock <= 0 || pieces < mod.buyPrice}
                    >
                      {mod.stock <= 0 ? 'OUT OF STOCK' : 'BUY'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Panel - Details */}
          <div style={styles.rightPanel}>
            {/* Total Stats */}
            {fitting && (
              <div style={styles.statsSection}>
                <div style={styles.statsHeader}>Total Module Bonuses</div>
                {renderModuleStats(fitting.stats)}
              </div>
            )}

            <div style={styles.statsSection}>
              <div style={styles.statsHeader}>Crew Bonuses</div>
              {Object.keys(crewBonuses).length === 0 ? (
                <div style={{ color: '#8b949e', fontSize: 12 }}>No bonuses</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {Object.entries(crewBonuses).map(([key, value]) => (
                    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: '#8b949e' }}>{formatCrewBonusLabel(key)}</span>
                      <span style={{ color: value >= 0 ? '#22c55e' : '#ef4444' }}>{value >= 0 ? '+' : ''}{value}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sigil Selection */}
            <div style={styles.statsSection}>
              <div style={styles.statsHeader}>Faction Sigil</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {equippedSigil ? (
                  <>
                    <div style={{
                      padding: 10,
                      backgroundColor: '#21262d',
                      border: `2px solid ${FACTION_INFO[equippedSigil]?.color || '#8b949e'}`,
                      borderRadius: 4,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <img
                          src={`${process.env.PUBLIC_URL || ''}${FACTION_INFO[equippedSigil]?.icon}`}
                          alt={FACTION_INFO[equippedSigil]?.name}
                          style={{ width: 40, height: 40, objectFit: 'contain' }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 'bold', color: FACTION_INFO[equippedSigil]?.color }}>
                            {FACTION_INFO[equippedSigil]?.name}
                          </div>
                          <div style={{ fontSize: 10, color: '#8b949e' }}>
                            Grade {sigilGrade || 1}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: 10, color: '#c9d1d9', lineHeight: 1.4 }}>
                        {FACTION_INFO[equippedSigil]?.description}
                      </div>
                    </div>
                    <button
                      style={{
                        ...styles.actionBtn,
                        ...styles.uninstallBtn,
                        fontSize: 11,
                        padding: 6,
                      }}
                      onClick={() => {
                        if (socket) {
                          socket.emit('faction:unequipSigil');
                        }
                      }}
                    >
                      UNEQUIP SIGIL
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 11, color: '#8b949e', padding: 8, backgroundColor: '#21262d', borderRadius: 4 }}>
                      Equip a faction sigil to gain reputation with that faction. Only one sigil can be equipped at a time.
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {['navy', 'pirate', 'merchant', 'smuggler'].map((faction) => (
                        <button
                          key={faction}
                          style={{
                            padding: 10,
                            backgroundColor: '#21262d',
                            border: `2px solid ${FACTION_INFO[faction]?.color || '#30363d'}`,
                            borderRadius: 4,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.backgroundColor = '#30363d';
                            (e.currentTarget as HTMLElement).style.borderColor = FACTION_INFO[faction]?.color || '#58a6ff';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d';
                            (e.currentTarget as HTMLElement).style.borderColor = FACTION_INFO[faction]?.color || '#30363d';
                          }}
                          onClick={() => {
                            if (socket) {
                              socket.emit('faction:equipSigil', { faction });
                            }
                          }}
                        >
                          <img
                            src={`${process.env.PUBLIC_URL || ''}${FACTION_INFO[faction]?.icon}`}
                            alt={FACTION_INFO[faction]?.name}
                            style={{ width: 32, height: 32, objectFit: 'contain' }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                          <div style={{ flex: 1, textAlign: 'left' }}>
                            <div style={{ fontSize: 11, fontWeight: 'bold', color: FACTION_INFO[faction]?.color }}>
                              {FACTION_INFO[faction]?.name}
                            </div>
                            <div style={{ fontSize: 9, color: '#8b949e', marginTop: 2 }}>
                              {FACTION_INFO[faction]?.description}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Selected Module */}
            {selectedModule && (
              <div style={styles.selectedModule}>
                <div style={{ ...styles.moduleName, color: RARITY_COLORS[selectedModule.rarity] }}>
                  {selectedModule.name}
                </div>
                <div style={styles.moduleDesc}>{selectedModule.description}</div>
                <div style={{ fontSize: '11px', color: '#8b949e', marginBottom: '8px' }}>
                  {SLOT_TYPE_LABELS[selectedModule.slotType]} Module | Tier {selectedModule.tier}
                </div>
                <div style={{ fontSize: '11px', marginBottom: '8px' }}>
                  <span style={{ color: '#f39c12' }}>Crew: {selectedModule.crewCost}</span>
                  {' | '}
                  <span style={{ color: '#9b59b6' }}>Cargo: {selectedModule.cargoCost}</span>
                </div>
                {renderModuleStats(selectedModule.stats)}

                {/* Actions */}
                {activeTab === 'overview' && selectedSlot && (
                  <>
                    {fitting?.modules[selectedSlot.type][selectedSlot.index] ? (
                      <button
                        style={{ ...styles.actionBtn, ...styles.uninstallBtn }}
                        onClick={handleUninstall}
                      >
                        UNINSTALL
                      </button>
                    ) : (
                      moduleInventory.some(m => m.id === selectedModule.id && m.slotType === selectedSlot.type) && (
                        <button
                          style={{ ...styles.actionBtn, ...styles.installBtn }}
                          onClick={handleInstall}
                        >
                          INSTALL
                        </button>
                      )
                    )}
                  </>
                )}

                {activeTab === 'inventory' && (
                  <button
                    style={{ ...styles.actionBtn, ...styles.uninstallBtn }}
                    onClick={() => handleSellModule(selectedModule.id)}
                  >
                    SELL FOR ₱{selectedModule.sellPrice.toLocaleString()}
                  </button>
                )}
              </div>
            )}

            {/* Quick Equip Panel (when clicking empty slot) */}
            {quickEquipPanelOpen && quickEquipSlot && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000,
              }} onClick={() => setQuickEquipPanelOpen(false)}>
                <div style={{
                  backgroundColor: '#0d1117',
                  border: '2px solid #30363d',
                  borderRadius: 8,
                  padding: 20,
                  maxWidth: 500,
                  maxHeight: '80vh',
                  overflowY: 'auto',
                  fontFamily: 'var(--font-mono), monospace',
                }} onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, color: '#58a6ff', fontSize: 16 }}>
                      Available {SLOT_TYPE_LABELS[quickEquipSlot.type]} Modules
                    </h3>
                    <button
                      onClick={() => setQuickEquipPanelOpen(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#8b949e',
                        fontSize: 24,
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    >
                      ×
                    </button>
                  </div>
                  
                  {moduleInventory.filter(m => m.slotType === quickEquipSlot.type).length === 0 ? (
                    <div style={{ color: '#8b949e', textAlign: 'center', padding: 20 }}>
                      No {SLOT_TYPE_LABELS[quickEquipSlot.type].toLowerCase()} modules in inventory
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {moduleInventory
                        .filter(m => m.slotType === quickEquipSlot.type)
                        .map((mod) => (
                          <div
                            key={mod.id}
                            style={{
                              padding: 12,
                              backgroundColor: '#21262d',
                              border: `1px solid ${RARITY_COLORS[mod.rarity]}`,
                              borderRadius: 4,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLElement).style.backgroundColor = '#30363d';
                              (e.currentTarget as HTMLElement).style.borderColor = '#58a6ff';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d';
                              (e.currentTarget as HTMLElement).style.borderColor = RARITY_COLORS[mod.rarity];
                            }}
                            onClick={() => {
                              setSelectedModule(mod);
                              setQuickEquipPanelOpen(false);
                            }}
                          >
                            {renderItemIcon(mod.icon, mod.name, 40)}
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 12, fontWeight: 'bold', color: RARITY_COLORS[mod.rarity] }}>
                                {mod.name}
                              </div>
                              <div style={{ fontSize: 10, color: '#8b949e', marginTop: 2 }}>
                                Crew: {mod.crewCost} | Cargo: {mod.cargoCost}
                              </div>
                            </div>
                            <button
                              style={{
                                ...styles.actionBtn,
                                ...styles.installBtn,
                                width: 'auto',
                                padding: '6px 12px',
                                fontSize: 11,
                                marginTop: 0,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedModule(mod);
                                setSelectedSlot(quickEquipSlot);
                                setQuickEquipPanelOpen(false);
                                // Trigger install after state updates
                                setTimeout(() => {
                                  if (socket) {
                                    socket.emit('modules:install', {
                                      shipId,
                                      shipType,
                                      moduleId: mod.id,
                                      slotType: quickEquipSlot.type,
                                      slotIndex: quickEquipSlot.index,
                                    }, (response: { success: boolean; message: string }) => {
                                      showMessage(response.message);
                                    });
                                  }
                                }, 0);
                              }}
                            >
                              EQUIP
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Module Inventory Quick View (when in overview tab) */}
            {activeTab === 'overview' && selectedSlot && !quickEquipPanelOpen && (
              <div style={styles.moduleInventory}>
                <div style={styles.inventoryHeader}>
                  Available {SLOT_TYPE_LABELS[selectedSlot.type]} Modules
                </div>
                <div style={styles.inventoryGrid}>
                  {moduleInventory
                    .filter(m => m.slotType === selectedSlot.type)
                    .map((mod, i) => (
                      <div
                        key={`inv-${mod.id}-${i}`}
                        style={{
                          ...styles.inventorySlot,
                          borderColor: selectedModule?.id === mod.id ? '#58a6ff' : RARITY_COLORS[mod.rarity],
                        }}
                        onClick={() => handleModuleClick(mod)}
                        title={mod.name}
                      >
                        {renderItemIcon(mod.icon, mod.name, 32)}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </OverlayPanel>
  );
};

export default ShipFittingPanel;
